import { hide, inline, offset, shift, useFloating, useTransitionStyles } from '@floating-ui/react';
import * as React from 'react';

import { isTextRangeSelection, type Value } from '@gitbook/slate';
import type { EditorContainer } from '@gitbook/slate-react';
import { getScrollParent, Portal, useEvent } from '@gitbook/spine-react';
import {
    useIsPointerPressed,
    usePersistentValue,
    useScrollListener,
} from '@gitbook/util-react-hooks';

import { FormatToolbar } from './FormatToolbar';
import { VirtualSelection, virtualSelectionElement } from './VirtualSelection';

const TRANSITION_DURATION = 150;
const OFFSET = 8;

export type FormatToolbarProps = {
    value: Value;
    editorRef: React.RefObject<EditorContainer>;
};

/**
 * Format toolbar floating on top of the native selection.
 */
export function FloatingFormatToolbar(props: FormatToolbarProps): React.ReactElement | null {
    const { value, editorRef } = props;
    // We maintain it mounted even if closed to have a close animation.
    const [isMounted, setIsMounted] = React.useState(false);
    const [editorElementRef] = React.useState(() => ({
        get current() {
            return editorRef.current?.elementRef.current ?? null;
        },
    }));
    const isPointerPressed = useIsPointerPressed(editorElementRef, { delayPointerUp: true });
    const isOpen =
        !value.isInVoid &&
        !!value.startBlock &&
        (!value.selection.isCollapsed || (isPointerPressed && isMounted)) &&
        !!value.selection.isFocused &&
        isTextRangeSelection(value.selection);

    React.useLayoutEffect(() => {
        if (!isOpen) {
            const timeout = setTimeout(() => {
                setIsMounted(false);
            }, TRANSITION_DURATION + 50);
            return () => clearTimeout(timeout);
        } else {
            setIsMounted(true);
        }
    }, [isOpen]);

    if (!isMounted) {
        return null;
    }

    return (
        <InnerFloatingFormatToolbar
            isOpen={isOpen}
            isPointerPressed={isPointerPressed}
            value={value}
        />
    );
}

type InnerFloatingFormatToolbarProps = {
    isOpen: boolean;
    isPointerPressed: boolean;
    value: Value;
};

/**
 * The core system of the floating format toolbar.
 */
function InnerFloatingFormatToolbar(props: InnerFloatingFormatToolbarProps) {
    const { isOpen, value, isPointerPressed } = props;

    // Anchor used to determine the boundary
    const anchorRef = React.useRef<HTMLDivElement>(null);

    // Boundary used to scope the floating element
    // we use the scroll parent of the anchor.
    const [boundary, setBoundary] = React.useState<Element | undefined>();

    // When the pointer is pressed, we don't show the toolbar,
    // so if pressed when mounted, we mark it as ready when released.
    const [isReady, setIsReady] = React.useState(!isPointerPressed);
    React.useLayoutEffect(() => {
        if (!isPointerPressed) {
            setIsReady(true);
        }
    }, [isPointerPressed]);

    // Floating hook used to position the overlay.
    const { refs, floatingStyles, update, middlewareData, context } = useFloating({
        placement: 'top',
        open: isOpen && isReady,
        middleware: [
            inline(),
            offset(OFFSET),
            shift({ padding: OFFSET, boundary, crossAxis: true, mainAxis: false }),
            hide({ boundary }),
        ],
    });

    // Transition of the overlay.
    const { isMounted, styles } = useTransitionStyles(context, {
        duration: 150,
        initial: {
            opacity: 0,
            scale: 0.8,
            transform: 'translateY(4px) scale(0.98)',
        },
    });

    // At mount, we set the position reference
    // and we compute the boundary.
    React.useLayoutEffect(() => {
        refs.setPositionReference(virtualSelectionElement);
        if (anchorRef.current) {
            setBoundary(getScrollParent(anchorRef.current));
        }
    }, [refs.setPositionReference]);

    // Update on scroll, resize and when the selection change.
    const windowRef = React.useRef(window);
    useScrollListener(update, windowRef, { capture: true });
    useEvent(windowRef, 'resize', update, { passive: true, capture: true });

    React.useLayoutEffect(() => {
        // We want value.selection as dependency to update when the selection changes.
        // If pointer is pressed, we want to wait for the release to update.
        if (
            isOpen &&
            isReady &&
            !value.selection.isCollapsed &&
            !window.getSelection()?.isCollapsed &&
            !isPointerPressed
        ) {
            update();
        }
    }, [value.selection, isReady, isOpen, isPointerPressed, update]);

    // The virtual selection ref is used to positionned the palette.
    const virtualSelectionRef = React.useRef<HTMLDivElement>(null);

    // Allow to keep the toolbar stable while we hide it or when the pointer is pressed.
    const persistentValue = usePersistentValue(
        isOpen && !isPointerPressed && !value.selection.isCollapsed && value.startBlock
            ? value
            : null
    );

    return (
        <>
            <div ref={anchorRef} />
            <Portal>
                <VirtualSelection ref={virtualSelectionRef} selection={value.selection} />
                <div
                    ref={refs.setFloating}
                    data-hide-on-drag
                    style={{
                        ...floatingStyles,
                        visibility: middlewareData.hide?.referenceHidden ? 'hidden' : undefined,
                    }}
                >
                    {isMounted && (
                        <div className="origin-bottom" style={styles}>
                            <FormatToolbar
                                paletteRef={virtualSelectionRef}
                                value={persistentValue ?? value}
                            />
                        </div>
                    )}
                </div>
            </Portal>
        </>
    );
}
